import { useEffect, useRef, useState, memo, useCallback } from 'react';

const projects = [
  {
    before: 'https://public.readdy.ai/ai/img_res/edited_f4da01bf693ce7f864c0898b5093f4fb_0876cacc.jpg',
    after: 'https://public.readdy.ai/ai/img_res/3ca9de8046b9d56cf1591ec9d3a2ece7.jpg',
    title: 'Fire & Smoke Damage Repair - Farmington, UT'
  },
  {
    before: 'https://public.readdy.ai/ai/img_res/edited_f39904c197ec95bee05aa533cdfc0169_dce77c7c.jpg',
    after: 'https://readdy.ai/api/search-image?query=Beautifully%20restored%20basement%20interior%20with%20fresh%20paint%20new%20flooring%20modern%20furniture%20and%20bright%20lighting%20showing%20complete%20renovation%20after%20water%20damage%20restoration%20clean%20and%20dry%20living%20space%20in%20Utah%20home&width=600&height=450&seq=gallery005a&orientation=landscape',
    title: 'Flood Restoration - Kaysville, UT'
  },
  {
    before: 'https://public.readdy.ai/ai/img_res/edited_943c47edfcf38b0536aae26f9ec05de1_7892473b.jpg',
    after: 'https://readdy.ai/api/search-image?query=Clean%20professional%20office%20interior%20with%20intact%20white%20drop%20ceiling%20tiles%20fluorescent%20lighting%20organized%20desks%20with%20computers%20office%20chairs%20neutral%20carpet%20well%20maintained%20commercial%20workspace%20tidy%20corporate%20environment%20modern%20business%20office%20pristine%20condition%20no%20damage&width=600&height=450&seq=gallery-office-clean-before-v2&orientation=landscape',
    title: 'Commercial Water Damage - Ogden, UT'
  }
];

function Gallery() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const nextSlide = useCallback(() => {
    if (projects.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % projects.length);
  }, []);

  const prevSlide = useCallback(() => {
    if (projects.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
  }, []);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      const nameInput = document.getElementById('name') as HTMLInputElement;
      nameInput?.focus();
    }, 500);
  }, []);

  return (
    <section
      id="gallery"
      ref={sectionRef}
      className="py-16 sm:py-24 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTEwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptMC0xMGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-sky-400 text-xl font-semibold mb-2">Our Work</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            Before &amp; After Transformations
          </h2>
          <p className="text-gray-300 text-lg sm:text-xl max-w-2xl mx-auto mb-8">
            See the dramatic results we achieve for Utah homeowners. Our job is to make your loss
            look like it never happened.
          </p>
        </div>

        {/* Slider container */}
        <div
          className={`relative transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="relative max-w-5xl mx-auto">
            {/* Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-6 sm:mb-8">
              {/* Before */}
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl group">
                <img
                  src={projects[currentIndex].before}
                  alt="Before"
                  className="w-full aspect-[4/3] sm:h-[300px] md:h-[400px] sm:aspect-auto object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg">
                  <span className="text-gray-900 font-semibold text-xs sm:text-sm">Before</span>
                </div>
              </div>

              {/* After */}
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl group">
                <img
                  src={projects[currentIndex].after}
                  alt="After"
                  className="w-full aspect-[4/3] sm:h-[300px] md:h-[400px] sm:aspect-auto object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4 bg-sky-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg">
                  <span className="text-white font-semibold text-xs sm:text-sm">After</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-4 sm:mb-6">
                {projects[currentIndex].title}
              </h3>
            </div>

            {/* Navigation buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-[calc((100vw-2rem)*3/8)] sm:top-[150px] md:top-[200px] -translate-y-1/2 -translate-x-1 sm:-translate-x-4 bg-white/90 backdrop-blur-sm p-2 sm:p-3 rounded-full shadow-lg hover:bg-white transition-all cursor-pointer z-10"
              aria-label="Previous slide"
            >
              <i className="ri-arrow-left-s-line text-xl sm:text-2xl text-gray-900"></i>
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-0 top-[calc((100vw-2rem)*3/8)] sm:top-[150px] md:top-[200px] -translate-y-1/2 translate-x-1 sm:translate-x-4 bg-white/90 backdrop-blur-sm p-2 sm:p-3 rounded-full shadow-lg hover:bg-white transition-all cursor-pointer z-10"
              aria-label="Next slide"
            >
              <i className="ri-arrow-right-s-line text-xl sm:text-2xl text-gray-900"></i>
            </button>
          </div>

          {/* Dots navigation */}
          <div className="flex justify-center gap-1 sm:gap-2 mt-6 sm:mt-8">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1 sm:h-3 rounded-full transition-all cursor-pointer ${
                  index === currentIndex ? 'w-4 sm:w-8 bg-sky-600' : 'w-1 sm:w-3 bg-white/50'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <button
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get Your Free Assessment
            </button>
            <a
              href="tel:3853406632"
              className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer border border-white/30"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now: (385) 340-6632
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default memo(Gallery);
